import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { NumberInput } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { increaseCampaignImpressionsBudget } from "@/lib/api";

function AddImpressionsDialogContent({
  campaignId,
  feedId,
  rate,
}: {
  campaignId: number;
  feedId: number;
  rate: number;
}) {
  const [impressions, setImpressions] = useState(0);
  const mutation = useMutation({
    mutationFn: async () => {
      await increaseCampaignImpressionsBudget({
        algoId: feedId,
        campaignId,
        impressions,
      });
    },
  });

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Add Impressions</AlertDialogTitle>
        <AlertDialogDescription>
          Add impressions to the campaign for the selected feed.
        </AlertDialogDescription>
      </AlertDialogHeader>

      <div className="flex flex-col gap-2 my-2">
        <Label htmlFor="impressions">Impressions</Label>
        <div className="flex items-center gap-2">
          <NumberInput
            id="impressions"
            className="flex-1"
            value={impressions}
            onValueChange={setImpressions}
          />
          <div className="text-green-dim bg-green-5 dark:bg-greendark-5 rounded-md p-2 w-fit">
            ${((rate * (impressions / 1000)) / 100).toFixed(2)}
          </div>
        </div>
      </div>

      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction
          isLoading={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Add Impressions
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  );
}

export function AddImpressionsDialog({
  campaignId,
  feedId,
  rate,
  children,
}: {
  campaignId: number;
  feedId: number;
  rate: number;
  children: React.ReactNode;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AddImpressionsDialogContent
          campaignId={campaignId}
          feedId={feedId}
          rate={rate}
        />
      </AlertDialogContent>
    </AlertDialog>
  );
}
